import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR, formatDate, todayISO } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Wallet, Landmark, ArrowDownCircle, ArrowUpCircle, Loader2, Trash2, RefreshCw } from "lucide-react";

export default function Cashier() {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [cashOut, setCashOut] = useState({ amount: "", kind: "cash", narration: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/cashier/summary", { params: { date } }).then((r) => {
      setData(r.data);
    });
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const submitCashOut = async (e) => {
    e.preventDefault();
    if (!cashOut.amount) return toast.error("Enter amount");
    setBusy(true);
    try {
      await api.post("/cashier/cash-out", {
        amount: parseFloat(cashOut.amount),
        kind: cashOut.kind,
        narration: cashOut.narration,
      });
      toast.success("Cash out logged");
      setCashOut({ amount: "", kind: "cash", narration: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const voidTxn = async (id) => {
    if (!window.confirm("Void this cash entry? This corrects the drawer total.")) return;
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Entry voided");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data) return <div className="text-slate-400 font-semibold">Loading…</div>;

  const Summary = ({ title, icon: Icon, opening, inAmt, outAmt, closing, color }) => (
    <div className="a1-card p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-6 h-6" style={{ color }} />
        <h3 className="font-head font-extrabold text-xl tracking-tight">{title}</h3>
      </div>
      <div className="space-y-2 text-base font-semibold">
        <Row label="Opening" value={formatINR(opening)} />
        <Row label="Total In" value={"+ " + formatINR(inAmt)} valueClass="text-[#16A34A]" />
        <Row label="Total Out" value={"- " + formatINR(outAmt)} valueClass="text-[#EA580C]" />
        <div className="border-t border-slate-300 my-2" />
        <div className="flex justify-between items-center">
          <span className="font-bold text-slate-600">Closing Balance</span>
          <span className="num font-head font-black text-2xl tracking-tight" style={{ color }}>{formatINR(closing)}</span>
        </div>
      </div>
    </div>
  );

  const Row = ({ label, value, valueClass = "" }) => (
    <div className="flex justify-between items-center">
      <span className="text-slate-500">{label}</span>
      <span className={`num font-bold ${valueClass}`}>{value}</span>
    </div>
  );

  return (
    <div className="a1-fade">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-head font-black text-3xl sm:text-4xl tracking-tight mb-1">Night Cashier</h1>
          <p className="text-slate-500 font-semibold">Daily cash drawer &amp; closing summary</p>
        </div>
        <div>
          <label className="a1-label">Date</label>
          <input data-testid="cashier-date" type="date" className="a1-input" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Summary title="Cash Drawer" icon={Wallet} opening={data.opening_cash} inAmt={data.cash_in} outAmt={data.cash_out} closing={data.closing_cash} color="#1E3A8A" />
        <Summary title="Bank / Online" icon={Landmark} opening={data.opening_bank} inAmt={data.bank_in} outAmt={data.bank_out} closing={data.closing_bank} color="#2563EB" />
      </div>

      <div className="a1-card p-4 mb-6 flex items-start gap-3 bg-[#F0F9FF] border-[#BAE6FD]">
        <RefreshCw className="w-5 h-5 text-[#0369A1] shrink-0 mt-0.5" />
        <p className="text-sm font-semibold text-slate-600">
          Opening balances roll forward automatically from the previous day's closing —
          no manual entry needed. Today's Opening Cash <span className="num font-bold text-[#0F172A]">{formatINR(data.opening_cash)}</span> and
          Opening Bank <span className="num font-bold text-[#0F172A]">{formatINR(data.opening_bank)}</span>.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Cash out */}
        <form onSubmit={submitCashOut} className="a1-card p-6">
          <h3 className="font-head font-extrabold text-xl tracking-tight mb-4">Log Money Given Out</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="a1-label">Amount</label>
              <input data-testid="cashout-amount" type="number" className="a1-input" value={cashOut.amount} onChange={(e) => setCashOut({ ...cashOut, amount: e.target.value })} />
            </div>
            <div>
              <label className="a1-label">From</label>
              <select data-testid="cashout-kind" className="a1-input" value={cashOut.kind} onChange={(e) => setCashOut({ ...cashOut, kind: e.target.value })}>
                <option value="cash">Cash</option>
                <option value="bank">Bank</option>
              </select>
            </div>
          </div>
          <label className="a1-label">Narration</label>
          <input data-testid="cashout-narration" className="a1-input mb-4" placeholder="Paid to staff / expense" value={cashOut.narration} onChange={(e) => setCashOut({ ...cashOut, narration: e.target.value })} />
          <button data-testid="cashout-submit" disabled={busy} className="a1-btn a1-btn-action w-full">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Log Cash Out"}
          </button>
        </form>
      </div>

      {/* Transactions */}
      <h3 className="font-head font-extrabold text-2xl tracking-tight mb-3">Today's Movements</h3>
      <div className="a1-card divide-y divide-slate-200">
        {data.transactions.length === 0 && <div className="p-6 text-slate-400 font-semibold">No transactions on this date.</div>}
        {data.transactions.map((t) => (
          <div key={t.id} className="p-4 flex items-center gap-3" data-testid={`txn-${t.id}`}>
            {t.direction === "in" ? (
              <ArrowDownCircle className="w-8 h-8 text-[#16A34A] shrink-0" />
            ) : (
              <ArrowUpCircle className="w-8 h-8 text-[#EA580C] shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <div className="font-bold truncate">{t.narration}</div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                {t.kind} • {t.mode}{t.utr ? ` • UTR ${t.utr}` : ""}
              </div>
            </div>
            <div className={`num font-head font-black text-lg ${t.direction === "in" ? "text-[#16A34A]" : "text-[#EA580C]"}`}>
              {t.direction === "in" ? "+" : "-"}{formatINR(t.amount)}
            </div>
            {t.ref_type === "manual" && (
              <button data-testid={`void-${t.id}`} onClick={() => voidTxn(t.id)} title="Void entry"
                className="shrink-0 text-slate-300 hover:text-red-500 transition-colors">
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
